import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Select,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { authService } from '../../../../utils/authService';
import { fetchAllTracksForCourses } from '../../../../utils/trackUtils';
import { fetchCoursesByTrack } from '../../../../utils/courseUtils';
import { fetchBatchesByCourseSingle } from '../../../../utils/batchUtils';
import { Session, Track, Course, Batch } from './sessionTypes';
import { asIdList } from './sessionUtils';

interface EditSessionModalProps {
  open: boolean;
  onClose: () => void;
  session: Session;
  onUpdate: () => void;
}

const firstId = (value: string | string[] | undefined | null): string =>
  asIdList(value)[0] || '';

/** Browser datetime-local value from an ISO / display datetime string. */
function toDatetimeLocalValue(raw?: string | null): string {
  const text = String(raw || '').trim();
  if (!text) return '';
  const d = new Date(text);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Compare datetimes at minute precision (datetime-local has no seconds). */
function floorToMinuteMs(value: Date): number {
  return Math.floor(value.getTime() / 60000) * 60000;
}

const EditSessionModal: React.FC<EditSessionModalProps> = ({ open, onClose, session, onUpdate }) => {
  const isPaused =
    String(session.session_status || '').trim().toLowerCase() === 'paused';
  const [formData, setFormData] = useState({
    session_title: session.Session_Topic,
    session_subject: '',
    session_meetlink: session.meetlink,
    start_date: session.Date,
    start_time: session.Start_Time,
    end_date: session.Expected_End_Time_date,
    end_time: session.Expected_End_Time_time,
    session_assigned_to: session.conductedby,
    track_id: firstId(session.track_id),
    course_id: firstId(session.course_id),
    batch_id: firstId(session.batch_id),
    subject_id: '',
    expected_resume_at: toDatetimeLocalValue(session.expected_resume_at),
  });
  const [isLoading, setIsLoading] = useState(false);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [subjects, setSubjects] = useState<string[]>([]);
  const [trainers, setTrainers] = useState<{ user_id: string; name: string }[]>([]);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [loadingTrainers, setLoadingTrainers] = useState(false);
  const [showOtherSubject, setShowOtherSubject] = useState(false);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const [availabilityMessage, setAvailabilityMessage] = useState('');
  const [conflictingSessions, setConflictingSessions] = useState<any[]>([]);
  const [sessionTitleError, setSessionTitleError] = useState<string>('');
  const [formError, setFormError] = useState<string>('');
  const [resumeError, setResumeError] = useState<string>('');

  // Load initial data
  useEffect(() => {
    if (open) {
      fetchTracks();
      fetchTrainers();
      setSessionTitleError('');
      setFormError('');
      setResumeError('');
      setFormData({
        session_title: session.Session_Topic,
        session_subject: session.subject || '',
        session_meetlink: session.meetlink,
        start_date: session.Date,
        start_time: session.Start_Time,
        end_date: session.Expected_End_Time_date,
        end_time: session.Expected_End_Time_time,
        session_assigned_to: session.session_assigned_to || '',
        track_id: firstId(session.track_id),
        course_id: firstId(session.course_id),
        batch_id: firstId(session.batch_id),
        subject_id: session.subject || '',
        expected_resume_at: toDatetimeLocalValue(session.expected_resume_at),
      });
    }
  }, [open, session]);

  useEffect(() => {
    if (open) {
      const originalDate = session.Date;
      const startDateInput = document.getElementById('edit-start-date') as HTMLInputElement;
      if (startDateInput) {
        startDateInput.min = originalDate;
      }
    }
  }, [open, session.Date]);

  useEffect(() => {
    if (formData.start_date) {
      setFormData(prev => ({ ...prev, end_date: formData.start_date }));
    }
  }, [formData.start_date]);

  useEffect(() => {
    if (formData.start_time) {
      const [hours, minutes] = formData.start_time.split(':').map(Number);
      const endTime = new Date();
      endTime.setHours(hours + 1, minutes, 0, 0);
      const endTimeString = endTime.toTimeString().slice(0, 5);
      setFormData(prev => ({ ...prev, end_time: endTimeString }));
    }
  }, [formData.start_time]);

  useEffect(() => {
    if (formData.start_date) {
      const endDateInput = document.getElementById('edit-end-date') as HTMLInputElement;
      if (endDateInput) {
        endDateInput.min = formData.start_date;
      }
    }
  }, [formData.start_date]);

  useEffect(() => {
    if (formData.start_date === session.Date) {
      const startTimeInput = document.getElementById('edit-start-time') as HTMLInputElement;
      if (startTimeInput) {
        startTimeInput.min = session.Start_Time;
      }
    }
  }, [formData.start_date, session.Date, session.Start_Time]);

  useEffect(() => {
    if (formData.start_date && formData.end_date && formData.start_time) {
      if (formData.start_date === formData.end_date) {
        const endTimeInput = document.getElementById('edit-end-time') as HTMLInputElement;
        if (endTimeInput) {
          endTimeInput.min = formData.start_time;
        }
      }
    }
  }, [formData.start_date, formData.end_date, formData.start_time]);

  useEffect(() => {
    if (formData.track_id) {
      fetchCourses(formData.track_id);
    }
  }, [formData.track_id]);

  useEffect(() => {
    if (formData.course_id) {
      fetchBatches(formData.course_id);
    }
  }, [formData.course_id]);

  useEffect(() => {
    if (formData.course_id && formData.batch_id) {
      fetchSubjects(formData.course_id, formData.batch_id);
    }
  }, [formData.course_id, formData.batch_id]);

  useEffect(() => {
    if (trainers.length > 0 && !formData.session_assigned_to && session.conductedby) {
      const trainer = trainers.find(t => t.name === session.conductedby);
      if (trainer) {
        setFormData(prev => ({ ...prev, session_assigned_to: trainer.user_id }));
      }
    }
  }, [trainers, session.conductedby, formData.session_assigned_to]);

  useEffect(() => {
    const courseId = firstId(session.course_id);
    const batchId = firstId(session.batch_id);
    if (open && courseId && batchId) {
      fetchSubjects(courseId, batchId);
    }
  }, [open, session.course_id, session.batch_id]);

  useEffect(() => {
    if (subjects.length > 0 && !formData.subject_id) {
      if (session.subject && subjects.includes(session.subject)) {
        setFormData(prev => ({ ...prev, subject_id: session.subject! }));
      } else {
        const firstSubject = subjects[0];
        if (firstSubject) {
          setFormData(prev => ({ ...prev, subject_id: firstSubject }));
        }
      }
    }
  }, [subjects, formData.subject_id, session.subject]);

  useEffect(() => {
    setShowOtherSubject(formData.subject_id === 'Other');
  }, [formData.subject_id]);

  const fetchTracks = async () => {
    try {
      const data = await fetchAllTracksForCourses();
      setTracks(data);
    } catch (error) {
      console.error("Error fetching tracks:", error);
    }
  };

  const fetchCourses = async (trackId: string) => {
    try {
      const data = await fetchCoursesByTrack(trackId);
      setCourses(data);
    } catch (error) {
      console.error("Error fetching courses:", error);
    }
  };

  const fetchBatches = async (courseId: string) => {
    try {
      const data = await fetchBatchesByCourseSingle(courseId);
      setBatches(data);
    } catch (error) {
      console.error("Error fetching batches:", error);
    }
  };

  const fetchSubjects = async (courseId: string, batchId: string) => {
    setLoadingSubjects(true);
    try {
      const response = await authService.get(`admin/batch/get_subject_for_batch/${courseId}/${batchId}/`);
      if (response.status === 200) {
        setSubjects(response.data.subjects || []);
      }
    } catch (error) {
      console.error("Error fetching subjects:", error);
    } finally {
      setLoadingSubjects(false);
    }
  };

  const fetchTrainers = async () => {
    setLoadingTrainers(true);
    try {
      const response = await authService.get('/admin/get_all_trainer_names/');
      if (response.status === 200) {
        setTrainers(response.data.trainers || []);
      }
    } catch (error) {
      console.error("Error fetching trainers:", error);
    } finally {
      setLoadingTrainers(false);
    }
  };

  const checkTrainerAvailability = async (): Promise<boolean> => {
    setIsCheckingAvailability(true);
    setAvailabilityMessage('');
    setConflictingSessions([]);
    
    try {
      const availabilityPayload = {
        trainer_id: formData.session_assigned_to || sessionStorage.getItem('Id') || '',
        start_date: formData.start_date,
        end_date: formData.end_date,
        start_time: `${formData.start_time}:00`,
        end_time: `${formData.end_time}:00`,
        // Exclude this session so reschedule does not conflict with itself.
        session_id: String(session.id || ''),
      };

      const response = await authService.post('/trainer/availability/', availabilityPayload);
      
      if (response.data.success === true) {
        setAvailabilityMessage('Trainer is available for the selected time slot.');
        return true;
      } else {
        setAvailabilityMessage('Trainer is not available for the selected time slot.');
        setConflictingSessions(response.data.list_of_sessions || response.data.conflicting_sessions || []);
        return false;
      }
    } catch (error) {
      console.error("Error checking trainer availability:", error);
      setAvailabilityMessage('Error checking trainer availability. Please try again.');
      return false;
    } finally {
      setIsCheckingAvailability(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setResumeError('');

    const originalDateTime = new Date(`${session.Date}T${session.Start_Time}`);
    const newDateTime = new Date(`${formData.start_date}T${formData.start_time}`);

    if (newDateTime < originalDateTime) {
      setFormError(
        'Cannot prepone the session. Start date/time must be the same as or later than the original session.',
      );
      return;
    }

    if (formData.start_date === formData.end_date && formData.start_time >= formData.end_time) {
      setFormError('End time must be after start time when dates are the same.');
      return;
    }

    const sessionTitle = formData.session_title;
    if (!sessionTitle || sessionTitle.trim().length <= 4) {
      setSessionTitleError('Session name too short');
      return;
    }
    setSessionTitleError('');

    if (isPaused) {
      const resumeRaw = String(formData.expected_resume_at || '').trim();
      if (!resumeRaw) {
        setResumeError('Expected resume date and time is required for a paused session.');
        return;
      }
      const resumeAt = new Date(resumeRaw);
      if (Number.isNaN(resumeAt.getTime())) {
        setResumeError('Enter a valid expected resume date and time.');
        return;
      }
      // Same minute as now is allowed; earlier than current time is not.
      const minAllowedMs = floorToMinuteMs(new Date());
      if (floorToMinuteMs(resumeAt) < minAllowedMs) {
        setResumeError(
          'Expected resume must be the current time or later. It cannot be earlier than now.',
        );
        return;
      }
    }

    const isAvailable = await checkTrainerAvailability();
    if (!isAvailable) {
      const conflictDetails =
        conflictingSessions.length > 0
          ? ` Conflicting sessions: ${conflictingSessions
              .map((s) => `${s.session_title} (${s.start_date} ${s.start_time})`)
              .join('; ')}`
          : '';
      setFormError(
        `Cannot update session. ${availabilityMessage || 'Trainer is not available.'}${conflictDetails}`,
      );
      return;
    }

    setIsLoading(true);
    try {
      const payload: Record<string, unknown> = {
        session_id: session.id.toString(),
        session_title: formData.session_title,
        session_subject:
          formData.subject_id === 'Other'
            ? formData.session_subject
            : formData.subject_id,
        session_meetlink: formData.session_meetlink,
        start_date: formData.start_date,
        start_time: formData.start_time,
        end_date: formData.end_date,
        end_time: formData.end_time,
        session_assigned_to: formData.session_assigned_to,
        // API expects list fields (same shape as create / other update callers).
        track_id: formData.track_id ? [formData.track_id] : [],
        course_id: formData.course_id ? [formData.course_id] : [],
        batch_id: formData.batch_id ? [formData.batch_id] : [],
        subject_id: formData.subject_id ? [formData.subject_id] : [],
      };
      if (isPaused && formData.expected_resume_at) {
        payload.expected_resume_at = new Date(
          formData.expected_resume_at,
        ).toISOString();
      }

      await authService.put('/session/update/', payload);
      onUpdate();
      onClose();
    } catch (error: any) {
      console.error('Error updating session:', error);
      const detail =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update session. Please try again.';
      setFormError(typeof detail === 'string' ? detail : 'Failed to update session.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formError) setFormError('');
    if (field === 'expected_resume_at' && resumeError) setResumeError('');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Session</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Session Title"
              value={formData.session_title}
              onChange={(e) => {
                handleInputChange('session_title', e.target.value);
                if (sessionTitleError) {
                  setSessionTitleError('');
                }
              }}
              fullWidth
              required
              error={!!sessionTitleError}
              helperText={sessionTitleError}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ fontSize: '12px', fontWeight: 'medium', mb: 0.5 }}>Track</Box>
                <Select
                  value={formData.track_id}
                  onChange={(e) => handleInputChange('track_id', e.target.value)}
                  disabled
                  fullWidth
                  size="small"
                >
                  <MenuItem value="">Select Track</MenuItem>
                  {tracks.map((track) => (
                    <MenuItem key={track.track_id} value={track.track_id}>
                      {track.track_name}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ fontSize: '12px', fontWeight: 'medium', mb: 0.5 }}>Course</Box>
                <Select
                  value={formData.course_id}
                  onChange={(e) => handleInputChange('course_id', e.target.value)}
                  disabled
                  fullWidth
                  size="small"
                >
                  <MenuItem value="">Select Course</MenuItem>
                  {courses.map((course) => (
                    <MenuItem key={course.course_id} value={course.course_id}>
                      {course.course_name}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ fontSize: '12px', fontWeight: 'medium', mb: 0.5 }}>Batch</Box>
                <Select
                  value={formData.batch_id}
                  onChange={(e) => handleInputChange('batch_id', e.target.value)}
                  disabled
                  fullWidth
                  size="small"
                >
                  <MenuItem value="">Select Batch</MenuItem>
                  {batches.map((batch) => (
                    <MenuItem key={batch.batch_id} value={batch.batch_id}>
                      {batch.batch_name}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Box>

            <Box>
              <Box sx={{ fontSize: '12px', fontWeight: 'medium', mb: 0.5 }}>Subject</Box>
              <Select
                value={formData.subject_id}
                onChange={(e) => handleInputChange('subject_id', e.target.value)}
                disabled={loadingSubjects || !formData.batch_id}
                fullWidth
                size="small"
              >
                <MenuItem value="">Select Subject</MenuItem>
                {subjects.map((subject, index) => (
                  <MenuItem key={index} value={subject}>
                    {subject}
                  </MenuItem>
                ))}
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </Box>

            {showOtherSubject && (
              <Box>
                <Box sx={{ fontSize: '12px', fontWeight: 'medium', mb: 0.5 }}>Specify Subject</Box>
                <TextField
                  value={formData.session_subject}
                  onChange={(e) => handleInputChange('session_subject', e.target.value)}
                  placeholder="Enter subject name"
                  fullWidth
                  size="small"
                  required
                />
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ fontSize: '12px', fontWeight: 'medium', mb: 0.5 }}>Start Date</Box>
                <TextField
                  id="edit-start-date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => handleInputChange('start_date', e.target.value)}
                  fullWidth
                  size="small"
                  required
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ fontSize: '12px', fontWeight: 'medium', mb: 0.5 }}>Start Time</Box>
                <TextField
                  id="edit-start-time"
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => handleInputChange('start_time', e.target.value)}
                  fullWidth
                  size="small"
                  required
                />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ fontSize: '12px', fontWeight: 'medium', mb: 0.5 }}>End Date</Box>
                <TextField
                  id="edit-end-date"
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => handleInputChange('end_date', e.target.value)}
                  fullWidth
                  size="small"
                  required
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ fontSize: '12px', fontWeight: 'medium', mb: 0.5 }}>End Time</Box>
                <TextField
                  id="edit-end-time"
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => handleInputChange('end_time', e.target.value)}
                  fullWidth
                  size="small"
                  required
                />
              </Box>
            </Box>

            <Box>
              <Box sx={{ fontSize: '12px', fontWeight: 'medium', mb: 0.5 }}>Trainer</Box>
              <Select
                value={formData.session_assigned_to}
                onChange={(e) => handleInputChange('session_assigned_to', e.target.value)}
                disabled={loadingTrainers}
                fullWidth
                size="small"
              >
                <MenuItem value="">Select Trainer</MenuItem>
                {trainers.map((trainer) => (
                  <MenuItem key={trainer.user_id} value={trainer.user_id}>
                    {trainer.name}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {isPaused ? (
              <TextField
                label="Expected resume"
                type="datetime-local"
                value={formData.expected_resume_at}
                onChange={(e) => handleInputChange('expected_resume_at', e.target.value)}
                fullWidth
                required
                error={!!resumeError}
                InputLabelProps={{ shrink: true }}
                helperText={
                  resumeError ||
                  'Must be the current date and time or later. Cannot be earlier than now.'
                }
              />
            ) : null}

            {formError ? (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  backgroundColor: '#ffeaea',
                  border: '1px solid #f44336',
                  color: '#c62828',
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formError}
                </Typography>
              </Box>
            ) : null}

            {availabilityMessage && (
              <Box sx={{ 
                p: 1, 
                borderRadius: 1, 
                backgroundColor: availabilityMessage.includes('available') ? '#e8f5e8' : '#ffeaea',
                border: `1px solid ${availabilityMessage.includes('available') ? '#4caf50' : '#f44336'}`,
                color: availabilityMessage.includes('available') ? '#2e7d32' : '#c62828'
              }}>
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  {availabilityMessage}
                </Typography>
                {conflictingSessions.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                      Conflicting Sessions:
                    </Typography>
                    {conflictingSessions.map((conflict, index) => (
                      <Typography key={index} variant="body2" sx={{ ml: 1, fontSize: '0.875rem' }}>
                        • {conflict.session_title} ({conflict.start_date} {conflict.start_time})
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={isLoading || isCheckingAvailability}>
            {isCheckingAvailability ? 'Checking Availability...' : 
             isLoading ? 'Updating...' : 'Update Session'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditSessionModal;

